"use client";

import * as React from "react";
import { frequencyCoverageDetail } from "../../lib/memberships/format-benefit";
import { cn } from "@baluu/ui/lib/utils";
import { Input } from "@baluu/ui/components/input";
import { Button } from "@baluu/ui/components/button";
import { Badge } from "@baluu/ui/components/badge";
import {
  Loader2,
  CreditCard,
  ExternalLink,
  Wallet,
  Gift,
  Tag,
  Lock,
  CheckCircle,
  Calendar,
  Clock,
  MapPin,
  ChevronDown,
  ShoppingBag,
  ShoppingCart,
  X,
  Sparkles,
  User,
  Plus,
  Info,
} from "lucide-react";
import { formatDiscountCode } from "./validation";
import { formatPrice } from "./utils";
import {
  includedFeesLabel,
} from "@baluu/ui/lib/checkout/booking-fee-disclosure";
import { TermsDialog, useTermsDialog } from "./terms-dialog";
import type { TicketPolicyEntry } from "./terms-dialog";
import type { TermsSettings, CustomTerms, TicketBookingPolicy } from "./utils/types";
import { TicketPolicyDialog } from "./ticket-selector";

const SCHOOLS_HIDE_TUTOR_NAME = ["bmDxPjPqe7QoNLk76", "3t7gZeu8pz7AiAeAM"];

type OrderItem = {
  name: string;
  description?: string;
  price: number;
  /** Authoritative line total (e.g. from tiered pricing). If provided, overrides price × count. */
  lineTotal?: number;
  /** Deposit line: `lineTotal` is the deposit due now; this is the full
   *  contract total for the same line, rendered muted after a slash
   *  (`£9.00/£49.00` — same pattern as the checkout summary card). */
  fullLineTotal?: number;
  count: number;
  type: "ticket" | "addon";
  imageUrl?: string;
  onRemove?: () => void;
  bookingPolicy?: TicketBookingPolicy | null;
};

type PaymentOption = "card" | "stripe" | "credits" | "bundle";

type FinalStepProps = {
  // Order details
  items: OrderItem[];
  dateLabel?: string;
  timeLabel?: string;
  locationLabel?: string;
  total: number;
  subtotal?: number;
  currency?: string;

  /**
   * Buyer-side service fee (Task 6/7), already folded into `total`. Renders
   * as a "Service fee" line between the subtotal and the total when > 0 —
   * DMCC Act 2025 requires the mandatory fee to be itemized, not silently
   * absorbed into a bare total. Always 0/undefined on non-Baluu-hosted
   * surfaces (webcomponent/iframe embeds), free bookings, and schools with
   * no fee configured — callers on those paths simply never pass a value.
   */
  serviceFeePence?: number;

  /**
   * School-set booking fee (customer-paid), already folded into `total`:
   * the session's quote once it exists, else the first-paint estimate
   * (`estimateBookingFeePence`). Renders as a "Booking fee" line ABOVE the
   * "Service fee" line when > 0, and is summed into the "(incl. … fee)" badge
   * beside the first price shown. 0 when the school has no fee or the basket
   * never carries one.
   */
  bookingFeePence?: number;

  /**
   * The rate and refund wording for the "Booking fee" row's tooltip
   * (`bookingFeeDetailText`). Omitted when the disclosure didn't load.
   */
  bookingFeeDetail?: string | null;

  /** Deposit balance top-up (balance payment link): when set, the inline order
   *  summary shows "total booking price − deposit already paid = balance due
   *  now" instead of a plain total. `total` is still the balance charged now. */
  depositContext?: { depositPaid: number; contractTotal: number };

  /** Deposit-at-checkout summary: when set, the Total row is labelled
   *  "Total Deposit" and a muted "Remaining balance" row renders under it.
   *  Distinct from `depositContext`, which is the balance TOP-UP flow. */
  depositTotals?: { remainingBalance: number };

  // Course/Product info for enhanced summary
  courseName?: string;
  courseThumbnail?: string;
  tutorName?: string;
  tutorImage?: string;

  // School identifier
  schoolId?: string;

  // Discount
  discount?: number;
  discountPercentage?: number;
  discountCode?: string;
  isDiscountApplied?: boolean;
  discountError?: string;
  isDiscountEnabled?: boolean;
  onApplyDiscount?: (code: string) => Promise<void>;
  onRemoveDiscount?: () => void;

  // Payment mode from school settings or API response
  paymentMode: "embedded" | "hosted";
  /** If hosted mode and we have a checkout URL ready */
  checkoutUrl?: string;
  isFreeBooking?: boolean;
  /** Whether payment will be handled via external portal (webcomponents) - hides card details section */
  isExternalPortalMode?: boolean;

  // State
  isLoading?: boolean;
  isSubmitting?: boolean;
  error?: string | null;

  // Bundle info (if paying with existing bundle - pre-selected from booking options)
  bundleInfo?: {
    name: string;
    ticketCount: number;
  };

  // Basket feature
  basketItemCount?: number;
  onAddToBasket?: () => void;

  // User's applicable bundles for "Pay with Bundle" payment option
  userApplicableBundles?: Array<{
    id: string;
    name: string;
    totalSessions: number;
    usedSessions: number;
    remainingSessions: number;
    expiredDate: string;
  }>;
  /** Currently selected bundle ID for payment (when user picks from bundle list) */
  selectedPaymentBundleId?: string;
  /** Called when user selects a bundle for payment */
  onSelectPaymentBundle?: (bundleId: string) => void;
  /** Called when user deselects bundle (switches back to card) */
  onDeselectPaymentBundle?: () => void;
  /** Called whenever the selected payment option changes (and on mount). Lets parents mirror the option so they can gate submission. */
  onPaymentOptionChange?: (option: PaymentOption) => void;

  // Membership credits for "Pay with Credits" payment option
  membershipResolution?: {
    subscriptionId: string;
    type: "credit" | "unlimited" | "frequency";
    creditsRemaining: number;
    coveredItemIndices: number[];
    planName: string;
    benefits?: Array<{
      benefitId: string;
      type: "credit" | "unlimited" | "frequency";
      usagesPerFrequency?: number;
      frequencyPeriod?: "week" | "month" | "days";
      frequencyDays?: number;
      usagesInWindow?: number;
    }>;
  } | null;
  /** Currently selected membership ID for payment */
  selectedMembershipId?: string | null;
  /** Called when user selects membership for payment */
  onSelectMembership?: (subscriptionId: string) => void;
  /** Called when user deselects membership */
  onDeselectMembership?: () => void;

  // Stripe form slot
  children?: React.ReactNode;

  /** Content rendered between the header and order summary (e.g. buyer details in basket checkout) */
  beforeOrderSummary?: React.ReactNode;

  // Cancellation policy (shown on mobile/tablet where sidebar is hidden)
  cancellationPolicyUrl?: string;
  termsSettings?: TermsSettings;
  customTerms?: CustomTerms;
  timezone?: string;
  ticketPolicies?: TicketPolicyEntry[];
  onOpenTermsDialog?: (data: {
    termsSettings?: TermsSettings;
    customTerms?: CustomTerms;
    timezone?: string;
    ticketPolicies?: TicketPolicyEntry[];
  }) => void;

  /** Callback when a per-ticket policy link is clicked (used in webcomponents for external portal) */
  onPolicyClick?: (ticketName: string, policy: TicketBookingPolicy) => void;

  /** Callback to trigger login flow. When provided, shows "Log in to use bundles" in payment methods. */
  onLogin?: () => void;

  /** Hide the inline Order Summary section (used when a sidebar summary is shown instead on desktop) */
  hideInlineOrderSummary?: boolean;

  /** Always show the Payment Method section, even when Stripe form (children) is provided.
   *  Used by basket checkout so users can switch payment method after the card form loads. */
  alwaysShowPaymentMethod?: boolean;

  className?: string;
};

// Schools that have discount/giftcard and payment method sections disabled
const SCHOOLS_WITH_DISABLED_CHECKOUT_SECTIONS = ["bmDxPjPqe7QoNLk76", "3t7gZeu8pz7AiAeAM"];

// Hook to detect mobile viewport
function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
}

// Collapsible Section Component with mobile awareness
type CollapsibleSectionProps = {
  title: string;
  icon: React.ReactNode;
  badge?: React.ReactNode;
  defaultOpen?: boolean;
  /** Force closed on mobile by default */
  mobileDefaultClosed?: boolean;
  children: React.ReactNode;
  className?: string;
};

function CollapsibleSection({
  title,
  icon,
  badge,
  defaultOpen = false,
  mobileDefaultClosed = true,
  children,
  className,
}: CollapsibleSectionProps) {
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = React.useState(() => {
    if (
      typeof window !== "undefined" &&
      window.innerWidth < 640 &&
      mobileDefaultClosed &&
      !defaultOpen
    ) {
      return false;
    }
    return defaultOpen;
  });

  React.useEffect(() => {
    if (isMobile && mobileDefaultClosed && !defaultOpen) {
      setIsOpen(false);
    }
  }, [isMobile, mobileDefaultClosed, defaultOpen]);

  return (
    <div
      className={cn(
        "bg-card rounded-xl border border-border overflow-hidden",
        isOpen && "shadow-md",
        !isOpen && "shadow-sm",
        className,
      )}
    >
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "w-full px-5 py-3.5 flex items-center justify-between transition-all duration-200",
          "min-h-[52px] touch-manipulation",
          isOpen
            ? "border-b border-border"
            : "hover:bg-muted/50",
        )}
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-2.5">
          {icon}
          <h3 className="font-semibold text-foreground uppercase text-sm tracking-wide">
            {title}
          </h3>
          {badge}
        </div>
        <ChevronDown className={cn(
          "w-4 h-4 text-muted-foreground transition-transform duration-200",
          isOpen && "rotate-180",
        )} />
      </button>
      <div
        className={cn(
          "transition-all duration-300 ease-in-out overflow-hidden",
          isOpen ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0",
        )}
      >
        {children}
      </div>
    </div>
  );
}

function FinalStep({
  items,
  dateLabel,
  timeLabel,
  locationLabel,
  total,
  subtotal,
  currency = "GBP",
  serviceFeePence = 0,
  bookingFeePence = 0,
  bookingFeeDetail = null,
  depositContext,
  depositTotals,
  courseName,
  courseThumbnail,
  tutorName,
  tutorImage,
  schoolId,
  discount = 0,
  discountPercentage,
  discountCode,
  isDiscountApplied = false,
  discountError,
  isDiscountEnabled = true,
  onApplyDiscount,
  onRemoveDiscount,
  paymentMode,
  checkoutUrl,
  isFreeBooking = false,
  isExternalPortalMode = false,
  isLoading = false,
  isSubmitting = false,
  error,
  bundleInfo,
  userApplicableBundles,
  selectedPaymentBundleId,
  onSelectPaymentBundle,
  onDeselectPaymentBundle,
  onPaymentOptionChange,
  membershipResolution,
  selectedMembershipId,
  onSelectMembership,
  onDeselectMembership,
  basketItemCount = 0,
  onAddToBasket,
  children,
  beforeOrderSummary,
  cancellationPolicyUrl,
  termsSettings,
  customTerms,
  timezone,
  ticketPolicies,
  onOpenTermsDialog,
  onPolicyClick,
  onLogin,
  hideInlineOrderSummary = false,
  alwaysShowPaymentMethod = false,
  className,
}: FinalStepProps) {
  const termsDialog = useTermsDialog();
  const hasTermsSettings = termsSettings?.cancellationPolicy || termsSettings?.reschedulePolicy || termsSettings?.termsAndConditions;

  // Check if this school has checkout sections disabled
  const hasCheckoutSectionsDisabled = schoolId
    ? SCHOOLS_WITH_DISABLED_CHECKOUT_SECTIONS.includes(schoolId)
    : false;
  // Determine actual payment mode - if we have a checkoutUrl, we're in hosted mode regardless of paymentMode prop
  const actualPaymentMode = checkoutUrl ? "hosted" : paymentMode;
  const [localDiscountCode, setLocalDiscountCode] = React.useState(
    discountCode || "",
  );
  const [isApplyingDiscount, setIsApplyingDiscount] = React.useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = React.useState(false);
  const [localDiscountError, setLocalDiscountError] = React.useState<
    string | null
  >(null);

  // Handle discount code input with auto-formatting
  const handleDiscountCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatDiscountCode(e.target.value);
    setLocalDiscountCode(formatted);
    // Clear error when user starts typing
    if (localDiscountError) {
      setLocalDiscountError(null);
    }
  };

  // Clear discount code
  const handleClearDiscount = () => {
    setLocalDiscountCode("");
    setShowSuccessAnimation(false);
    setLocalDiscountError(null);
  };
  const [selectedPaymentOption, setSelectedPaymentOption] =
    React.useState<PaymentOption>(() => {
      // If the parent already has a bundle selected on mount (e.g. customer
      // came in from the bundle-manage Timetable tab), default the radio to
      // "bundle" so the UI matches the pre-selected payment context.
      if (selectedPaymentBundleId) return "bundle";
      return actualPaymentMode === "hosted" ? "stripe" : "card";
    });
  const paymentFormRef = React.useRef<HTMLDivElement>(null);

  // Sync selectedPaymentOption when actualPaymentMode changes
  // This handles the case when checkoutUrl arrives async and changes the mode
  React.useEffect(() => {
    if (actualPaymentMode === "hosted" && selectedPaymentOption === "card") {
      setSelectedPaymentOption("stripe");
    } else if (
      actualPaymentMode === "embedded" &&
      selectedPaymentOption === "stripe"
    ) {
      setSelectedPaymentOption("card");
    }
  }, [actualPaymentMode, selectedPaymentOption]);

  // Handle payment option change
  const handlePaymentOptionChange = (optionId: PaymentOption) => {
    setSelectedPaymentOption(optionId);
    // If switching away from bundle, deselect the bundle
    if (optionId !== "bundle" && selectedPaymentBundleId) {
      onDeselectPaymentBundle?.();
    }
    // If switching away from credits, deselect the membership
    if (optionId !== "credits" && selectedMembershipId) {
      onDeselectMembership?.();
    }
    // If selecting credits, select the membership
    if (optionId === "credits" && membershipResolution) {
      onSelectMembership?.(membershipResolution.subscriptionId);
    }
    // If selecting bundle and no bundle is picked yet, auto-select the first viable one
    // so users can't accidentally fall through to Stripe without picking a bundle.
    if (
      optionId === "bundle" &&
      !selectedPaymentBundleId &&
      viableBundles &&
      viableBundles.length > 0
    ) {
      onSelectPaymentBundle?.(viableBundles[0].id);
    }
  };

  // Mirror the currently-selected payment option up to the parent so it can
  // gate submission (e.g. block Stripe session creation when "bundle" is
  // selected without a bundle picked). Fires on mount and on every change.
  React.useEffect(() => {
    onPaymentOptionChange?.(selectedPaymentOption);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPaymentOption]);

  const [policyDialog, setPolicyDialog] = React.useState<{
    isOpen: boolean;
    ticketName: string;
    policy: TicketBookingPolicy | null;
  }>({ isOpen: false, ticketName: "", policy: null });

  const ticketItems = items.filter((item) => item.type === "ticket");
  const addonItems = items.filter((item) => item.type === "addon");
  const hasItems = items.some((item) => item.count > 0);
  const calculatedSubtotal =
    subtotal ?? items.reduce((sum, item) => sum + (item.lineTotal ?? item.price * item.count), 0);

  const totalItemCount = items.reduce((sum, item) => sum + item.count, 0);
  const totalTicketCount = ticketItems.reduce((sum, item) => sum + item.count, 0);

  // Filter bundles to only those with enough remaining sessions to cover all tickets
  const viableBundles = userApplicableBundles?.filter(
    (bundle) => bundle.remainingSessions >= totalTicketCount,
  );
  const hasBundles = (viableBundles?.length ?? 0) > 0;

  const handleApplyDiscount = async () => {
    if (!onApplyDiscount || !localDiscountCode.trim()) return;
    setIsApplyingDiscount(true);
    setShowSuccessAnimation(false);
    setLocalDiscountError(null);
    try {
      await onApplyDiscount(localDiscountCode);
      // Show success animation briefly
      setShowSuccessAnimation(true);
      setTimeout(() => setShowSuccessAnimation(false), 2000);
    } catch (error) {
      // Display error message from backend or default message
      const errorMessage =
        error instanceof Error
          ? error.message
          : typeof error === "string"
            ? error
            : "Discount code could not be applied";
      setLocalDiscountError(errorMessage);
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  // Payment options configuration
  const paymentOptions: {
    id: PaymentOption;
    label: string;
    description: string;
    icon: React.ReactNode;
    disabled: boolean;
    disabledReason?: string;
    available: boolean;
  }[] = [
    {
      id: "card",
      label: "Pay with Card",
      description: "Pay securely with your credit or debit card",
      icon: <CreditCard className="w-5 h-5" />,
      disabled: false,
      available: actualPaymentMode === "embedded",
    },
    {
      id: "stripe",
      label: "Pay with Card",
      description:
        "You'll be redirected to Stripe page to complete payment where you can pay with your credit or debit cardYou will be redirected to Stripe to securely complete your payment using a credit or debit card.",
      icon: <ExternalLink className="w-5 h-5" />,
      disabled: false,
      available: actualPaymentMode === "hosted",
    },
    {
      id: "credits",
      label: "Pay with Credits",
      description: membershipResolution
        ? membershipResolution.type === "credit"
          ? `Use ${membershipResolution.coveredItemIndices.length} credit${membershipResolution.coveredItemIndices.length !== 1 ? "s" : ""} from ${membershipResolution.planName} (${membershipResolution.creditsRemaining} remaining)`
          : membershipResolution.type === "frequency"
          ? (() => {
              const detail = frequencyCoverageDetail(membershipResolution.benefits);
              return `Covered by ${membershipResolution.planName}${detail ? ` (${detail})` : ""}`;
            })()
          : `Covered by ${membershipResolution.planName} (Unlimited)`
        : "No membership credits available",
      icon: <Wallet className="w-5 h-5" />,
      disabled: !membershipResolution || membershipResolution.type === "status",
      disabledReason: membershipResolution
        ? membershipResolution.type === "status" ? "Status memberships unlock special tickets" : undefined
        : "No membership",
      available: !!membershipResolution && membershipResolution.type !== "status",
    },
    {
      id: "bundle",
      label: "Pay with Bundle",
      description: hasBundles
        ? `Use tickets from your bundle (${viableBundles!.length} available)`
        : "No applicable bundles found",
      icon: <Gift className="w-5 h-5" />,
      disabled: !hasBundles,
      disabledReason: hasBundles ? undefined : "No bundles",
      // Callers that don't deal with bundles at all (e.g. the webcomponents
      // embed with no auth) leave userApplicableBundles undefined — hide the
      // option entirely in that case to avoid showing "No applicable bundles
      // found" to anonymous buyers who can't do anything about it.
      available: !onLogin && userApplicableBundles !== undefined,
    },
  ];

  const availableOptions = paymentOptions.filter((opt) => opt.available);
  const enabledOptions = availableOptions.filter((opt) => !opt.disabled);
  // A picked bundle/credits option that stops being offered (signed out, the
  // basket changed) falls back to the card, else the parent's submit gate
  // stays shut with no radio left to change.
  // Bundles still loading (undefined while signed in) don't count as gone, so
  // a bundle pre-selected on mount survives until they arrive.
  const pickedOptionGone =
    (selectedPaymentOption === "credits" ||
      (selectedPaymentOption === "bundle" &&
        (!!onLogin || userApplicableBundles !== undefined))) &&
    !enabledOptions.some((opt) => opt.id === selectedPaymentOption);
  React.useEffect(() => {
    if (pickedOptionGone) {
      setSelectedPaymentOption(actualPaymentMode === "hosted" ? "stripe" : "card");
    }
  }, [pickedOptionGone, actualPaymentMode]);
  const hasMultiplePaymentOptions = enabledOptions.length > 1;
  // Show login prompt whenever the user is logged out (onLogin callback is only wired when
  // !isLoggedIn). Bundles and membership credits are now resolved against the session email,
  // so logged-out users must sign in to use either payment option.
  const showLoginPrompt = !!onLogin;

  return (
    <div className={cn("space-y-5", className)}>
      {beforeOrderSummary}

      {/* Order Summary Section - Collapsible (hidden on desktop when sidebar summary is shown) */}
      <CollapsibleSection
        className={hideInlineOrderSummary ? "lg:hidden" : undefined}
        title="Order Summary"
        icon={<ShoppingBag className="w-4 h-4 text-muted-foreground" />}
        badge={
          <div className="flex items-center gap-2 ml-2">
            <Badge variant="secondary" className="text-xs">
              {totalItemCount} {totalItemCount === 1 ? "item" : "items"}
            </Badge>
            <span className="text-sm font-semibold tabular-nums text-foreground">
              {formatPrice(total, currency)}
            </span>
            {/* Task 7 — DMCC Act 2025: the mandatory fee must be disclosed
             *  beside the price at the very first point a price is shown,
             *  not sprung at payment. This badge (collapsed by default) is
             *  the first price the customer sees on this component. */}
            {(bookingFeePence > 0 || serviceFeePence > 0) && (
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                (incl.{" "}
                {formatPrice(bookingFeePence + serviceFeePence, currency)}{" "}
                {includedFeesLabel(bookingFeePence, serviceFeePence)})
              </span>
            )}
          </div>
        }
        defaultOpen={false}
        mobileDefaultClosed={true}
      >
        {/* Course/Product Header with Thumbnail */}
        {(() => {
          const shouldHideTutor = schoolId && SCHOOLS_HIDE_TUTOR_NAME.includes(schoolId);
          const showTutor = tutorName && !shouldHideTutor;
          if (!(courseName || courseThumbnail || showTutor)) return null;
          return (
            <div className="px-4 py-4 border-b border-border/50 bg-gradient-to-br from-background via-card to-background">
              <div className="flex gap-4">
                {courseThumbnail && (
                  <div className="shrink-0">
                    <img
                      src={courseThumbnail}
                      alt={courseName || "Course"}
                      className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg shadow-sm"
                    />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  {courseName && (
                    <h4 className="font-semibold text-foreground line-clamp-2">
                      {courseName}
                    </h4>
                  )}
                  {showTutor && (
                    <div className="flex items-center gap-2 mt-2">
                      {tutorImage ? (
                        <img
                          src={tutorImage}
                          alt={tutorName}
                          className="w-6 h-6 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-card-hover flex items-center justify-center">
                          <User className="w-3 h-3 text-muted-foreground" />
                        </div>
                      )}
                      <span className="text-sm text-muted-foreground">
                        {tutorName}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Date/Time/Location */}
        {(dateLabel || timeLabel || locationLabel) && (
          <div className="px-4 py-3 border-b border-border/50 bg-blue-100 dark:bg-blue-900/50">
            <div className="flex flex-wrap gap-3 sm:gap-4 text-sm">
              {dateLabel && (
                <span className="flex items-center gap-1.5 text-foreground">
                  <Calendar className="w-4 h-4 text-blue-600 shrink-0" />
                  <span className="truncate">{dateLabel}</span>
                </span>
              )}
              {timeLabel && (
                <span className="flex items-center gap-1.5 text-foreground">
                  <Clock className="w-4 h-4 text-blue-600 shrink-0" />
                  {timeLabel}
                </span>
              )}
              {locationLabel && (
                <span className="flex items-center gap-1.5 text-foreground">
                  <MapPin className="w-4 h-4 text-blue-600 shrink-0" />
                  <span className="truncate">{locationLabel}</span>
                </span>
              )}
            </div>
          </div>
        )}

        {/* Tickets */}
        {ticketItems.filter((item) => item.count > 0).length > 0 && (
          <div className="border-b border-border/50">
            <div className="px-4 py-2 bg-background">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Tickets
              </span>
            </div>
            {ticketItems
              .filter((item) => item.count > 0)
              .map((item, index) => (
                <div
                  key={`ticket-${index}`}
                  className="px-4 py-3 flex items-start gap-3"
                >
                  {item.imageUrl && (
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      className="w-14 h-14 rounded-lg object-cover shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground text-sm">
                      {item.name} × {item.count}
                    </p>
                    {item.description && (
                      // Wraps rather than truncating: this line carries the
                      // booking window ("Thu 18 Jun, 10:00-11:30 (1h 30m)"),
                      // and an ellipsis here hid the END of the booking on the
                      // last screen before payment. items-start keeps the icon
                      // on the first line when it wraps.
                      <p className="text-xs text-muted-foreground mt-0.5 flex items-start gap-1">
                        <Calendar className="w-3 h-3 shrink-0 mt-0.5" />
                        <span>{item.description}</span>
                      </p>
                    )}
                    {item.bookingPolicy && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onPolicyClick) {
                            onPolicyClick(item.name, item.bookingPolicy!);
                          } else {
                            setPolicyDialog({
                              isOpen: true,
                              ticketName: item.name,
                              policy: item.bookingPolicy!,
                            });
                          }
                        }}
                        className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 font-medium mt-1 text-left transition-colors"
                      >
                        Cancellation Policy
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {!bundleInfo && (
                      <span className="font-medium text-foreground tabular-nums text-sm">
                        {item.fullLineTotal != null ? (
                          <>
                            {formatPrice(item.lineTotal ?? item.price * item.count, currency)}
                            /
                            <span className="text-muted-foreground">
                              {formatPrice(item.fullLineTotal, currency)}
                            </span>
                          </>
                        ) : (
                          formatPrice(item.lineTotal ?? item.price * item.count, currency)
                        )}
                      </span>
                    )}
                    {item.onRemove && (
                      <button
                        type="button"
                        onClick={item.onRemove}
                        className="p-1.5 rounded-full border border-border bg-muted text-muted-foreground hover:text-destructive hover:border-destructive/40 hover:bg-destructive/10 transition-colors"
                        aria-label={`Remove ${item.name}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* Add-ons */}
        {addonItems.filter((item) => item.count > 0).length > 0 && (
          <div className="border-b border-border/50">
            <div className="px-4 py-2 bg-background">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Add-ons
              </span>
            </div>
            {addonItems
              .filter((item) => item.count > 0)
              .map((item, index) => (
                <div
                  key={`addon-${index}`}
                  className="px-4 py-3 flex justify-between items-start"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground">
                      {item.name} × {item.count}
                    </p>
                    {item.description && (
                      <p className="text-sm text-muted-foreground truncate">
                        {item.description}
                      </p>
                    )}
                  </div>
                  <span className="font-medium text-foreground ml-4 shrink-0">
                    {formatPrice(item.lineTotal ?? item.price * item.count, currency)}
                  </span>
                </div>
              ))}
          </div>
        )}

        {/* Bundle Info */}
        {bundleInfo && (
          <div className="px-4 py-3 border-b border-border/50 bg-blue-100 dark:bg-blue-900">
            <p className="text-sm text-foreground font-medium">
              We will deduct {bundleInfo.ticketCount}{" "}
              {bundleInfo.ticketCount === 1 ? "ticket" : "tickets"} from your{" "}
              <strong>{bundleInfo.name}</strong> bundle
            </p>
          </div>
        )}

        {/* Subtotal & Discount & Total */}
        {hasItems && (
          <div className="px-4 py-4">
            {/* Subtotal */}
            {(discount > 0 || serviceFeePence > 0 || bookingFeePence > 0) && (
              <div className="flex justify-between items-center text-sm text-muted-foreground mb-2">
                <span>Subtotal</span>
                <span className="tabular-nums">{formatPrice(calculatedSubtotal, currency)}</span>
              </div>
            )}

            {/* Booking fee (Task 13 — school-set customer-paid fee), rendered
             *  ABOVE the service fee. */}
            {bookingFeePence > 0 && (
              <div className="flex justify-between items-center text-sm text-muted-foreground mb-2">
                <span className="flex items-center gap-1">
                  Booking fee
                  {bookingFeeDetail && (
                    <span title={bookingFeeDetail} className="inline-flex">
                      <Info className="h-3.5 w-3.5" aria-hidden="true" />
                      <span className="sr-only">{bookingFeeDetail}</span>
                    </span>
                  )}
                </span>
                <span className="tabular-nums">{formatPrice(bookingFeePence, currency)}</span>
              </div>
            )}

            {/* Service fee (Task 7 — DMCC Act 2025 up-front disclosure) */}
            {serviceFeePence > 0 && (
              <div className="flex justify-between items-center text-sm text-muted-foreground mb-2">
                <span>Service fee</span>
                <span className="tabular-nums">{formatPrice(serviceFeePence, currency)}</span>
              </div>
            )}

            {/* Applied Discount */}
            {discount > 0 && (
              <div className="flex justify-between items-center text-sm text-green-600 dark:text-green-400 mb-2">
                <span className="flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Discount
                  {discountPercentage && (
                    <Badge
                      variant="secondary"
                      className="text-xs bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400"
                    >
                      {discountPercentage}% off
                    </Badge>
                  )}
                </span>
                <span className="tabular-nums">-{formatPrice(discount, currency)}</span>
              </div>
            )}

            {/* Deposit balance top-up: full price − deposit already paid. */}
            {depositContext && (
              <>
                <div className="flex justify-between items-center text-sm text-muted-foreground mb-2">
                  <span>Total booking price</span>
                  <span className="tabular-nums">{formatPrice(depositContext.contractTotal, currency)}</span>
                </div>
                <div className="flex justify-between items-center text-sm text-green-600 dark:text-green-400 mb-2">
                  <span className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Deposit already paid
                  </span>
                  <span className="tabular-nums">-{formatPrice(depositContext.depositPaid, currency)}</span>
                </div>
              </>
            )}

            {/* Total */}
            <div className="flex justify-between items-center pt-3 mt-1 border-t border-border">
              <span className="font-semibold text-foreground">
                {depositContext
                  ? "Balance due now"
                  : depositTotals
                    ? "Total Deposit"
                    : "Total"}
              </span>
              <span className="font-bold text-lg text-foreground tabular-nums">
                {bundleInfo || selectedPaymentBundleId
                  ? "Free (Bundle)"
                  : formatPrice(total, currency)}
              </span>
            </div>

            {/* Deposit at checkout: what's still owed after today's deposit */}
            {depositTotals && depositTotals.remainingBalance > 0 && (
              <div className="flex justify-between items-center mt-2 text-sm text-muted-foreground">
                <span className="font-medium">Remaining balance</span>
                <span className="font-medium tabular-nums">
                  {formatPrice(depositTotals.remainingBalance, currency)}
                </span>
              </div>
            )}
          </div>
        )}
      </CollapsibleSection>

      {/* Discount/Gift Card Section - Collapsible */}
      {/* Show if discount is enabled, OR if a discount is already applied (so user can remove it) */}
      {/* Hidden for schools with checkout sections disabled */}
      {!hasCheckoutSectionsDisabled &&
        ((!isFreeBooking && !bundleInfo && isDiscountEnabled) ||
          isDiscountApplied) && (
          <CollapsibleSection
            title="Discount or Gift Card"
            icon={<Gift className="w-4 h-4 text-muted-foreground" />}
            badge={
              isDiscountApplied && discount > 0 ? (
                <Badge className="bg-green-100 dark:bg-green-900-muted border-green-500/30 text-xs ml-2">
                  Applied
                </Badge>
              ) : undefined
            }
            defaultOpen={false}
            mobileDefaultClosed={true}
          >
            <div className="p-4">
              {!isDiscountApplied ? (
                <>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input
                        type="text"
                        placeholder="Enter discount code"
                        value={localDiscountCode}
                        onChange={handleDiscountCodeChange}
                        className={cn(
                          "pr-10 font-mono tracking-wider min-h-[48px]",
                          showSuccessAnimation &&
                            "border-green-500 bg-green-100 dark:bg-green-900-muted",
                        )}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleApplyDiscount();
                        }}
                        maxLength={19}
                      />
                      {/* Clear button */}
                      {localDiscountCode && (
                        <button
                          type="button"
                          onClick={handleClearDiscount}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-card-hover transition-colors touch-manipulation"
                          aria-label="Clear code"
                        >
                          <X className="w-4 h-4 text-muted-foreground/70" />
                        </button>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      onClick={handleApplyDiscount}
                      disabled={isApplyingDiscount || !localDiscountCode.trim()}
                      className={cn(
                        "min-h-[48px] min-w-[80px] touch-manipulation",
                        showSuccessAnimation &&
                          "border-green-500 text-green-500 bg-green-100 dark:bg-green-900-muted",
                      )}
                    >
                      {isApplyingDiscount ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : showSuccessAnimation ? (
                        <span className="flex items-center gap-1">
                          <Sparkles className="w-4 h-4" />
                          Valid!
                        </span>
                      ) : (
                        "Apply"
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Enter a discount code or gift card to apply savings
                  </p>
                  {(discountError || localDiscountError) && (
                    <p className="text-destructive mt-2 flex items-center gap-1">
                      <X className="w-4 h-4 shrink-0" />
                      {localDiscountError || discountError}
                    </p>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-between bg-green-100 dark:bg-green-900-muted rounded-lg p-4 animate-in fade-in duration-300">
                  <span className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 shrink-0 text-green-500" />
                    <span className="font-medium">Discount applied</span>
                    {discountPercentage && (
                      <Badge className="bg-green-100 dark:bg-green-900-muted text-green-500 border-green-500/30 hidden sm:inline-flex">
                        {discountPercentage}% off
                      </Badge>
                    )}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-green-500">
                      -{formatPrice(discount, currency)}
                    </span>
                    {onRemoveDiscount && (
                      <button
                        type="button"
                        onClick={() => {
                          onRemoveDiscount();
                          setLocalDiscountCode("");
                          setShowSuccessAnimation(false);
                          setLocalDiscountError(null);
                        }}
                        className="text-muted-foreground hover:text-destructive transition-colors p-1 rounded-full hover:bg-destructive/10/10"
                        aria-label="Remove discount"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CollapsibleSection>
        )}

      {/* Add to Basket Option - shown when basket is enabled */}
      {onAddToBasket && !bundleInfo && (
        <div className="rounded-lg border-2 border-dashed border-primary/40 bg-primary/5 p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-primary/10 rounded-full">
                <ShoppingCart className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">
                  Add to Basket
                </h3>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {basketItemCount > 0
                    ? `You have ${basketItemCount} item${basketItemCount === 1 ? "" : "s"} in your basket. Add this and checkout together.`
                    : "Add this to your basket and continue browsing more classes."}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={onAddToBasket}
              className="w-full sm:w-auto shrink-0 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add to Basket
            </Button>
          </div>
        </div>
      )}

      {/* Payment Method Selection - Collapsible */}
      {/* Show when multiple options, login prompt, hosted mode, no Stripe form yet, or explicitly forced visible */}
      {!hasCheckoutSectionsDisabled && !isFreeBooking && !bundleInfo && (hasMultiplePaymentOptions || showLoginPrompt || actualPaymentMode === "hosted" || !children || alwaysShowPaymentMethod) && (
        <CollapsibleSection
          title="Payment Method"
          icon={<CreditCard className="w-4 h-4 text-muted-foreground" />}
          defaultOpen={actualPaymentMode === "hosted" || hasMultiplePaymentOptions || showLoginPrompt || !children || alwaysShowPaymentMethod}
          mobileDefaultClosed={actualPaymentMode === "embedded" && !hasMultiplePaymentOptions && !showLoginPrompt && !!children}
        >
          <div className="p-4 space-y-3">
            {availableOptions.map((option) => (
              <label
                key={option.id}
                onClick={() => {
                  if (!option.disabled) {
                    handlePaymentOptionChange(option.id);
                    // Scroll to payment form when clicking "Pay with Card"
                    if (
                      option.id === "card" &&
                      actualPaymentMode === "embedded"
                    ) {
                      setTimeout(() => {
                        paymentFormRef.current?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }, 200);
                    }
                  }
                }}
                className={cn(
                  "flex items-start gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all",
                  "min-h-[72px] touch-manipulation",
                  option.disabled
                    ? "opacity-50 cursor-not-allowed bg-background border-border"
                    : selectedPaymentOption === option.id
                      ? "border-primary bg-primary/20 shadow-sm"
                      : "border-border hover:border-muted-foreground/50 hover:bg-background/50",
                )}
              >
                <input
                  type="radio"
                  name="paymentOption"
                  value={option.id}
                  checked={selectedPaymentOption === option.id}
                  onChange={() => {
                    if (!option.disabled) {
                      handlePaymentOptionChange(option.id);
                    }
                  }}
                  disabled={option.disabled}
                  className="mt-1 w-5 h-5 text-primary focus:ring-primary"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {option.icon}
                    <span className="font-medium text-foreground">
                      {option.label}
                    </span>
                    {option.disabled && option.disabledReason && (
                      <Badge variant="secondary" className="text-xs">
                        {option.disabledReason}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {option.description}
                  </p>
                </div>
              </label>
            ))}

            {/* Bundle Selection List - shown when "Pay with Bundle" is selected */}
            {selectedPaymentOption === "bundle" &&
              hasBundles &&
              viableBundles && (
                <div className="mt-3 space-y-2">
                  <p className="text-sm font-medium text-foreground">
                    Select a bundle:
                  </p>
                  {viableBundles.map((bundle) => {
                    const isSelected = selectedPaymentBundleId === bundle.id;
                    const expiryDate = new Date(
                      bundle.expiredDate,
                    ).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    });
                    return (
                      <button
                        key={bundle.id}
                        type="button"
                        onClick={() => onSelectPaymentBundle?.(bundle.id)}
                        className={cn(
                          "w-full text-left p-3 rounded-lg border-2 transition-all",
                          "min-h-[60px] touch-manipulation",
                          isSelected
                            ? "border-primary bg-primary/10 shadow-sm"
                            : "border-border hover:border-muted-foreground/50 hover:bg-background/50",
                        )}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground text-sm truncate">
                              {bundle.name}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {bundle.remainingSessions} of{" "}
                              {bundle.totalSessions} sessions remaining
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Expires: {expiryDate}
                            </p>
                          </div>
                          {isSelected && (
                            <CheckCircle className="w-5 h-5 text-primary shrink-0 ml-2" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

            {/* Login prompt - shown when user is not logged in and onLogin is available */}
            {showLoginPrompt && (
              <div className="mt-3 p-4 rounded-lg border-2 border-dashed border-border bg-background/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-full shrink-0">
                    <Gift className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      Have a bundle or membership?
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Log in to use your bundle tickets or membership credits
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onLogin}
                    className="shrink-0 min-h-[36px] touch-manipulation"
                  >
                    Log in
                  </Button>
                </div>
              </div>
            )}
          </div>

        </CollapsibleSection>
      )}

      {/* Stripe Redirect Notice - Only shown for hosted payment mode */}
      {!isFreeBooking && !bundleInfo && !selectedPaymentBundleId && actualPaymentMode === "hosted" && (
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="flex items-start gap-3">
            <ExternalLink className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">
                Secure Stripe Payment
              </p>
              <p className="text-sm text-muted-foreground">
                Please click the button below to be redirected to Stripe to
                complete your payment securely.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Card Details Section - Only shown when Stripe form (children) is provided */}
      {!isFreeBooking &&
        !bundleInfo &&
        !selectedPaymentBundleId &&
        !isExternalPortalMode &&
        selectedPaymentOption === "card" &&
        actualPaymentMode === "embedded" &&
        children && (
          <CollapsibleSection
            title="Card Details"
            icon={<CreditCard className="w-4 h-4 text-muted-foreground" />}
            defaultOpen={true}
            mobileDefaultClosed={false}
          >
            <div ref={paymentFormRef} className="p-4">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground/70 mx-auto" />
                    <p className="mt-3 text-muted-foreground">
                      Loading payment form...
                    </p>
                  </div>
                </div>
              ) : (
                children
              )}
            </div>

            {/* Security Notice */}
            <div className="px-4 py-3 border-t border-border/50 bg-gradient-to-r from-background to-card">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Lock className="w-4 h-4 shrink-0" />
                <span>Your payment is secure and encrypted</span>
              </div>
            </div>
          </CollapsibleSection>
        )}

      {/* Standalone Security Notice - shown when Card Details section is not visible */}
      {!isFreeBooking && !bundleInfo && !selectedPaymentBundleId && !isExternalPortalMode &&
        !(selectedPaymentOption === "card" && actualPaymentMode === "embedded" && children) && (
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-2">
          <Lock className="w-3.5 h-3.5 shrink-0" />
          <span>Your payment is secure and encrypted</span>
        </div>
      )}

      {/* External Portal Mode Notice */}
      {isExternalPortalMode && !isFreeBooking && !bundleInfo && !selectedPaymentBundleId && (
        <div className="p-6 bg-gradient-to-r from-blue-100 to-blue-100 dark:from-blue-900 dark:to-blue-900/70 border border-blue-500/30 rounded-lg shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-full">
              <CreditCard className="w-6 h-6 text-blue-700 dark:text-blue-300" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-700 dark:text-blue-300">
                Secure Payment
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                Click "Continue to Payment" to enter your card details securely.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Free Booking Notice */}
      {isFreeBooking && (
        <div className="p-6 bg-gradient-to-r from-green-100 to-green-100 dark:from-green-900 dark:to-green-900/70 border border-green-500/30 rounded-lg shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900-muted rounded-full">
              <CheckCircle className="w-6 h-6 " />
            </div>
            <div>
              <h3 className="font-semibold ">No Payment Required</h3>
              <p className="text-sm mt-1">
                This booking is free of charge. Click continue to complete your
                booking.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Bundle Payment Notice (pre-selected from booking options) */}
      {bundleInfo && (
        <div className="p-6 bg-gradient-to-r from-blue-100 to-blue-100 dark:from-blue-900 dark:to-blue-900/70 border border-blue-500/30 rounded-lg shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-full">
              <Gift className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-600">
                Paying with Bundle
              </h3>
              <p className="text-sm text-blue-600/80 mt-1">
                Your booking will be completed using your bundle credits. Click
                continue to confirm.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Dynamic Bundle Payment Notice (selected from payment method) */}
      {!bundleInfo && selectedPaymentBundleId && viableBundles && (
        (() => {
          const selectedBundle = viableBundles.find(
            (b) => b.id === selectedPaymentBundleId,
          );
          if (!selectedBundle) return null;
          return (
            <div className="p-6 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/30 dark:to-blue-900/30 border border-purple-300/50 dark:border-purple-700/50 rounded-lg shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-full">
                  <Gift className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-purple-700 dark:text-purple-300">
                    Paying with Bundle
                  </h3>
                  <p className="text-sm text-purple-600/80 dark:text-purple-400/80 mt-1">
                    Using <strong>{selectedBundle.name}</strong> &mdash;{" "}
                    {selectedBundle.remainingSessions} sessions remaining. Click
                    confirm to complete your booking.
                  </p>
                </div>
              </div>
            </div>
          );
        })()
      )}

      {/* Membership Credits Payment Notice */}
      {selectedMembershipId && membershipResolution && (
        <div className="p-6 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 border border-emerald-300/50 dark:border-emerald-700/50 rounded-lg shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 dark:bg-emerald-900 rounded-full">
              <Wallet className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-emerald-700 dark:text-emerald-300">
                Paying with Membership Credits
              </h3>
              <p className="text-sm text-emerald-600/80 dark:text-emerald-400/80 mt-1">
                {membershipResolution.type === "credit"
                  ? <>Using <strong>{membershipResolution.coveredItemIndices.length}</strong> credit{membershipResolution.coveredItemIndices.length !== 1 ? "s" : ""} from <strong>{membershipResolution.planName}</strong>. {membershipResolution.creditsRemaining - membershipResolution.coveredItemIndices.length} credits remaining after this booking.</>
                  : membershipResolution.type === "frequency"
                  ? (() => {
                      // No "remaining this period": the resolver's usage is
                      // this period's, and the basket may be for another one.
                      const detail = frequencyCoverageDetail(membershipResolution.benefits);
                      return <>Covered by <strong>{membershipResolution.planName}</strong>{detail ? <> ({detail})</> : null}. Click confirm to complete your booking.</>;
                    })()
                  : <>Covered by <strong>{membershipResolution.planName}</strong> (Unlimited). Click confirm to complete your booking.</>}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-destructive/10-muted border border-destructive/30 rounded-lg shadow-sm">
          <p className="text-sm text-destructive font-medium">{error}</p>
        </div>
      )}

      {/* Cancellation Policy - visible on mobile/tablet where sidebar is hidden */}
      {cancellationPolicyUrl && (
        <div className="lg:hidden">
          {hasTermsSettings ? (
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                if (onOpenTermsDialog) {
                  onOpenTermsDialog({ termsSettings, customTerms, timezone, ticketPolicies });
                } else {
                  termsDialog.open();
                }
              }}
              className="text-sm text-primary hover:text-primary/80 font-medium"
            >
              Cancellation Policy
            </button>
          ) : (
            <a
              href={cancellationPolicyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:text-primary/80 font-medium"
            >
              Cancellation Policy
            </a>
          )}
        </div>
      )}

      {/* Terms Dialog for cancellation policy */}
      <TermsDialog
        isOpen={termsDialog.isOpen}
        onClose={termsDialog.close}
        settings={termsSettings}
        customTerms={customTerms}
        timezone={timezone}
        ticketPolicies={ticketPolicies}
      />

      {/* Per-ticket policy dialog (inline, for non-webcomponents) */}
      {!onPolicyClick && policyDialog.policy && (
        <TicketPolicyDialog
          isOpen={policyDialog.isOpen}
          onClose={() => setPolicyDialog((prev) => ({ ...prev, isOpen: false }))}
          ticketName={policyDialog.ticketName}
          policy={policyDialog.policy}
        />
      )}

      {/* Submitting Overlay */}
      {isSubmitting && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
          <div className="bg-card rounded-xl p-8 text-center shadow-2xl mx-4 max-w-sm">
            <div className="relative">
              <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
              <div className="absolute inset-0 -z-10 bg-primary/20 blur-xl rounded-full" />
            </div>
            <p className="mt-4 text-foreground font-semibold text-lg">
              {isFreeBooking || bundleInfo || selectedPaymentBundleId || selectedMembershipId
                ? "Confirming booking..."
                : "Processing payment..."}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Please don&apos;t close this page
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export { FinalStep };
export type { FinalStepProps, OrderItem, PaymentOption };
